import { useEffect, useMemo, useState } from 'react'
import Input from '@/components/forms/Input/Input'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import { useServices, useServiceOfferings, useSelectServices, useDeleteServiceOffering } from '@/hooks/useServices'
import './Steps.css'

type ServicePricingStepProps = {
    selectedServiceIds: number[]
    onComplete?: () => void
}

type Service = {
    id: number
    name: string
    description: string
    pricing_type: 'hourly' | 'quote'
}

type ServiceConfig = {
    serviceId: number
    pricingType: 'hourly' | 'quote'
    hourlyRate: string
    minimumHours: string
    baseQuoteAmount: string
    quoteNotes: string
    requiresVisit: boolean
    description?: string
}

const defaultConfig = (service: Service): ServiceConfig => ({
    serviceId: service.id,
    pricingType: service.pricing_type,
    hourlyRate: '',
    minimumHours: '1',
    baseQuoteAmount: '',
    quoteNotes: '',
    requiresVisit: false,
    description: '',
})

const ServicePricingStep = ({ selectedServiceIds, onComplete }: ServicePricingStepProps) => {
    const { data: servicesData, isLoading: servicesLoading } = useServices(150)
    const { data: offeringsData, isLoading: offeringsLoading } = useServiceOfferings()
    const selectServicesMutation = useSelectServices()
    const deleteOfferingMutation = useDeleteServiceOffering()

    const [serviceConfigs, setServiceConfigs] = useState<Record<number, ServiceConfig>>({})
    const [saving, setSaving] = useState(false)

    const services = useMemo<Service[]>(() => servicesData?.results || [], [servicesData])
    const loading = servicesLoading || offeringsLoading

    const selectedServices = useMemo(() => {
        if (!selectedServiceIds.length) return [] as Service[]
        return services.filter(service => selectedServiceIds.includes(service.id))
    }, [selectedServiceIds, services])

    useEffect(() => {
        if (!selectedServices.length) {
            setServiceConfigs({})
            return
        }

        const initial: Record<number, ServiceConfig> = {}

        selectedServices.forEach(service => {
            const existing = Array.isArray(offeringsData)
                ? offeringsData.find((offering: any) => {
                      const serviceId = typeof offering.service === 'number' ? offering.service : offering.service?.id
                      return serviceId === service.id
                  })
                : null

            if (existing) {
                initial[service.id] = {
                    serviceId: service.id,
                    pricingType: service.pricing_type,
                    hourlyRate: existing.hourly_rate != null ? String(existing.hourly_rate) : '',
                    minimumHours: existing.minimum_hours != null ? String(existing.minimum_hours) : '1',
                    baseQuoteAmount: existing.base_quote_amount != null ? String(existing.base_quote_amount) : '',
                    quoteNotes: existing.quote_notes || '',
                    requiresVisit: Boolean(existing.requires_quote_visit),
                    description: existing.description || '',
                }
            } else {
                initial[service.id] = defaultConfig(service)
            }
        })

        setServiceConfigs(initial)
    }, [offeringsData, selectedServices])

    const updateServiceConfig = (service: Service, updater: (config: ServiceConfig) => ServiceConfig) => {
        setServiceConfigs(prev => ({
            ...prev,
            [service.id]: updater(prev[service.id] || defaultConfig(service)),
        }))
    }

    const handleSave = async () => {
        if (!selectedServices.length) {
            alert('Selecciona al menos un servicio antes de configurar precios.')
            return
        }

        try {
            setSaving(true)
            const payload = selectedServices.map(service => {
                const config = serviceConfigs[service.id] || defaultConfig(service)

                if (config.pricingType === 'hourly') {
                    const rate = Number(config.hourlyRate)
                    if (!config.hourlyRate || Number.isNaN(rate) || rate <= 0) {
                        throw new Error('Define una tarifa horaria válida para todos los servicios por hora.')
                    }
                    const minHours = Number(config.minimumHours)
                    if (Number.isNaN(minHours) || minHours < 1) {
                        throw new Error('Las horas mínimas deben ser un número mayor o igual a 1.')
                    }
                }

                if (config.pricingType === 'quote' && config.baseQuoteAmount) {
                    const base = Number(config.baseQuoteAmount)
                    if (Number.isNaN(base) || base < 0) {
                        throw new Error('El monto base de cotización debe ser un número positivo.')
                    }
                }

                return {
                    service_id: config.serviceId,
                    pricing_type: config.pricingType,
                    hourly_rate: config.pricingType === 'hourly' ? Number(config.hourlyRate) : null,
                    minimum_hours: config.pricingType === 'hourly' ? Number(config.minimumHours || '1') : null,
                    base_quote_amount: config.pricingType === 'quote' && config.baseQuoteAmount ? Number(config.baseQuoteAmount) : null,
                    quote_notes: config.pricingType === 'quote' ? config.quoteNotes : '',
                    requires_quote_visit: config.pricingType === 'quote' ? config.requiresVisit : false,
                    description: config.description,
                }
            })

            await selectServicesMutation.mutateAsync({ services: payload })

            if (Array.isArray(offeringsData)) {
                const offeringsToRemove = offeringsData.filter((offering: any) => {
                    const serviceId = typeof offering.service === 'number' ? offering.service : offering.service?.id
                    return serviceId && !selectedServiceIds.includes(serviceId)
                })

                if (offeringsToRemove.length) {
                    await Promise.all(
                        offeringsToRemove.map((offering: any) => deleteOfferingMutation.mutateAsync(offering.id))
                    )
                }
            }

            alert('Información de servicios guardada correctamente')
            onComplete?.()
        } catch (error: any) {
            if (error instanceof Error && !error.message.includes('mutateAsync')) {
                alert(error.message)
            } else {
                console.error('Error guardando servicios:', error)
                alert(error?.response?.data?.error || 'No pudimos guardar la configuración de tus servicios.')
            }
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="step-container">
                <div className="step-loading" style={{ padding: '3rem 0' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem' }}>Cargando servicios seleccionados…</p>
                </div>
            </div>
        )
    }

    if (!selectedServices.length) {
        return (
            <div className="step-container">
                <div className="step-empty-selection">
                    <h4>Primero elige tus servicios</h4>
                    <p>Regresa al paso anterior y selecciona al menos un servicio para configurar tarifas y detalles.</p>
                </div>
            </div>
        )
    }

    return (
        <div className="step-container">
            <div className="step-section">
                <h4 className="step-section-title">Configura precios y detalles</h4>
                <p className="step-hint">
                    Ingresa tarifas claras y notas útiles para cada servicio. Esta información es la que verán tus clientes.
                </p>

                <div className="step-service-config-grid">
                    {selectedServices.map((service) => {
                        const config = serviceConfigs[service.id] || defaultConfig(service)

                        return (
                            <div key={service.id} className="step-service-config-card">
                                <div className="step-service-config-header">
                                    <div>
                                        <h5>{service.name}</h5>
                                        {service.description && <p className="step-hint">{service.description}</p>}
                                    </div>
                                    <span className={`step-pricing-pill step-pricing-pill--${service.pricing_type}`}>
                                        {service.pricing_type === 'hourly' ? 'Cobro por hora' : 'Cotización'}
                                    </span>
                                </div>

                                {service.pricing_type === 'hourly' ? (
                                    <div className="step-input-row">
                                        <Input
                                            name={`hourlyRate-${service.id}`}
                                            label="Tarifa por hora (S/)"
                                            placeholder="Ej: 40.00"
                                            value={config.hourlyRate}
                                            setValue={(value) =>
                                                updateServiceConfig(service, prev => ({
                                                    ...prev,
                                                    hourlyRate: value,
                                                }))
                                            }
                                            type="number"
                                            isRequired
                                        />
                                        <Input
                                            name={`minimumHours-${service.id}`}
                                            label="Horas mínimas"
                                            placeholder="1"
                                            value={config.minimumHours}
                                            setValue={(value) =>
                                                updateServiceConfig(service, prev => ({
                                                    ...prev,
                                                    minimumHours: value,
                                                }))
                                            }
                                            type="number"
                                            isRequired
                                        />
                                    </div>
                                ) : (
                                    <div className="step-input-row">
                                        <Input
                                            name={`baseQuoteAmount-${service.id}`}
                                            label="Monto base (opcional)"
                                            placeholder="Monto mínimo de servicio en soles"
                                            value={config.baseQuoteAmount}
                                            setValue={(value) =>
                                                updateServiceConfig(service, prev => ({
                                                    ...prev,
                                                    baseQuoteAmount: value,
                                                }))
                                            }
                                            type="number"
                                        />
                                        <Input
                                            name={`quoteNotes-${service.id}`}
                                            label="Notas para cotizaciones"
                                            placeholder="Describe qué incluye la cotización"
                                            value={config.quoteNotes}
                                            setValue={(value) =>
                                                updateServiceConfig(service, prev => ({
                                                    ...prev,
                                                    quoteNotes: value,
                                                }))
                                            }
                                        />
                                    </div>
                                )}

                                {service.pricing_type === 'quote' && (
                                    <label className="step-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={config.requiresVisit}
                                            onChange={(event) =>
                                                updateServiceConfig(service, prev => ({
                                                    ...prev,
                                                    requiresVisit: event.target.checked,
                                                }))
                                            }
                                        />
                                        Requiero coordinar una visita antes de enviar la cotización
                                    </label>
                                )}

                                <Input
                                    name={`serviceDescription-${service.id}`}
                                    label="Descripción para el cliente"
                                    placeholder="Cuenta brevemente qué incluye este servicio."
                                    value={config.description || ''}
                                    setValue={(value) =>
                                        updateServiceConfig(service, prev => ({
                                            ...prev,
                                            description: value,
                                        }))
                                    }
                                    multiline
                                    rows={4}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="step-actions">
                <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Guardando...' : 'Guardar servicios'}
                </Button>
            </div>
        </div>
    )
}

export default ServicePricingStep
